/**
 * Australian financial year helpers (1 July → 30 June).
 * Used for FY-native projection rows: optional partial first row from anchor to 30 June.
 */

/** June 30 that closes the FY containing `date`. */
export function getAustralianFyEndDateContaining(date: Date | string): Date {
    const d = date instanceof Date ? new Date(date.getTime()) : new Date(date);
    const y = d.getFullYear();
    const m = d.getMonth();
    const endYear = m >= 6 ? y + 1 : y;
    return new Date(endYear, 5, 30);
}

/** July 1 at the start of the FY containing `date`. */
export function getAustralianFyStartDateContaining(date: Date | string): Date {
    const end = getAustralianFyEndDateContaining(date);
    return new Date(end.getFullYear() - 1, 6, 1);
}

/** Inclusive day count in local calendar (no DST shift within AU date-only use). */
export function daysInclusive(start: Date, end: Date): number {
    const s = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
    const e = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
    return Math.floor((e - s) / 86400000) + 1;
}

/** e.g. FY25/26, given fyStart (July 1) and fyEnd (June 30). */
export function formatAustralianFyLabel(fyStart: Date, fyEnd: Date): string {
    const a = fyStart.getFullYear() % 100;
    const b = fyEnd.getFullYear() % 100;
    return `FY${String(a).padStart(2, '0')}/${String(b).padStart(2, '0')}`;
}

export interface AustralianFyProjectionPeriod {
    periodIndex: number;
    periodStart: Date;
    periodEnd: Date;
    fyLabel: string;
    fyEndingYear: number;
    yearFraction: number;
    isPartialFy: boolean;
    capitalExpensePeriodStart: Date;
    capitalExpensePeriodEnd: Date;
}

/**
 * Build consecutive FY projection periods from a day-precise anchor.
 * Row 1 runs from anchor (inclusive) to the June 30 closing that FY (may be partial).
 * Later rows are full FYs (July 1 → next June 30).
 *
 * A hand-kept copy of the server's `support/financialYearHelpers.ts` — keep the two in step.
 */
export function buildAustralianFyProjectionPeriods(anchorDate: Date | string, rowCount: number): AustralianFyProjectionPeriod[] {
    const anchor = anchorDate instanceof Date ? new Date(anchorDate.getTime()) : new Date(anchorDate);
    const cursorStart = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
    const periods: AustralianFyProjectionPeriod[] = [];

    for (let i = 0; i < rowCount; i++) {
        const fyEndThisRow = getAustralianFyEndDateContaining(cursorStart);
        const fyStartThisRow = getAustralianFyStartDateContaining(cursorStart);
        const fullFyDays = daysInclusive(fyStartThisRow, fyEndThisRow);
        const periodEnd = new Date(fyEndThisRow.getTime());
        const periodStart = new Date(cursorStart.getTime());
        const periodDays = daysInclusive(periodStart, periodEnd);
        const yearFraction = fullFyDays > 0 ? periodDays / fullFyDays : 1;
        const isPartialFy = periodDays < fullFyDays;

        periods.push({
            periodIndex: i + 1,
            periodStart,
            periodEnd,
            fyLabel: formatAustralianFyLabel(fyStartThisRow, fyEndThisRow),
            fyEndingYear: fyEndThisRow.getFullYear(),
            yearFraction,
            isPartialFy,
            capitalExpensePeriodStart: new Date(periodStart.getTime()),
            capitalExpensePeriodEnd: new Date(periodEnd.getTime()),
        });

        const nextJuly = new Date(periodEnd.getFullYear(), 6, 1);
        cursorStart.setTime(nextJuly.getTime());
    }

    return periods;
}
